import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PlantData } from '../types';
import { Dna, Sprout, Zap, Brain, Star, Calendar, Grid, Loader2 } from 'lucide-react';

const getRarityColor = (rarity: string) => {
  switch (rarity) {
    case 'Legendary': return 'text-yellow-400 border-yellow-400/20 bg-yellow-400/5';
    case 'Rare': return 'text-blue-400 border-blue-400/20 bg-blue-400/5';
    case 'Common': return 'text-green-400 border-green-400/20 bg-green-400/5';
    default: return 'text-gray-400 border-gray-400/20 bg-gray-400/5';
  }
};

const getRarityIcon = (rarity: string) => {
  switch (rarity) {
    case 'Legendary': return <Star className="w-4 h-4" />;
    case 'Rare': return <Brain className="w-4 h-4" />;
    case 'Common': return <Sprout className="w-4 h-4" />;
    default: return <Dna className="w-4 h-4" />;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export function Garden() {
  const [plants, setPlants] = useState<PlantData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof PlantData | 'created_at';
    direction: 'asc' | 'desc';
  }>({ key: 'created_at', direction: 'desc' });
  const [viewMode, setViewMode] = useState<'table' | 'visual'>('table');
  const [isVisualLoading, setIsVisualLoading] = useState(false);

  useEffect(() => {
    const loadPlants = async () => {
      try {
        const { data, error: plantsError } = await supabase
          .from('plants')
          .select('*')
          .order('created_at', { ascending: false });

        if (plantsError) throw plantsError;
        setPlants(data || []);
      } catch (err: any) {
        console.error('Error loading plants:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlants();

    // Subscribe to changes
    const subscription = supabase
      .channel('plants')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'plants' 
      }, () => {
        loadPlants();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const sortPlants = (a: PlantData, b: PlantData) => {
    let aValue = a[sortConfig.key as keyof PlantData];
    let bValue = b[sortConfig.key as keyof PlantData];

    // Special handling for dates
    if (sortConfig.key === 'created_at') {
      aValue = new Date(a.created_at || '').getTime();
      bValue = new Date(b.created_at || '').getTime();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  };

  const requestSort = (key: keyof PlantData | 'created_at') => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleViewModeChange = (mode: 'table' | 'visual') => {
    if (mode === 'visual') {
      setIsVisualLoading(true);
      // This is just for show - it will keep loading forever
      setViewMode('visual');
    } else {
      setIsVisualLoading(false);
      setViewMode('table');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black pt-[72px] flex items-center justify-center">
        <div className="text-gray-400">Loading garden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black pt-[72px] flex items-center justify-center">
        <div className="text-red-400">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-[72px]">
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-black/50 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Sprout className="w-5 h-5" />
                  Digital Garden
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  {plants.length} plants growing in the garden
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewModeChange('table')}
                  className={`px-3 py-2 rounded transition-colors ${
                    viewMode === 'table' 
                      ? 'bg-white text-black' 
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Table View
                </button>
                <button
                  onClick={() => handleViewModeChange('visual')}
                  className={`px-3 py-2 rounded transition-colors flex items-center gap-2 ${
                    viewMode === 'visual'
                      ? 'bg-white text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                  disabled={isVisualLoading}
                >
                  {isVisualLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Visual View
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'visual' ? (
            <div className="h-[600px] flex items-center justify-center">
              <div className="flex flex-col items-center gap-4 text-gray-400">
                <Loader2 className="w-8 h-8 animate-spin" />
                <p>Loading visual view...</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="p-4 text-left">
                      <button 
                        onClick={() => requestSort('species')}
                        className="flex items-center gap-2 hover:text-green-400 transition-colors"
                      >
                        Species
                        {sortConfig.key === 'species' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="p-4 text-left">
                      <button 
                        onClick={() => requestSort('rarity')}
                        className="flex items-center gap-2 hover:text-green-400 transition-colors"
                      >
                        Rarity
                        {sortConfig.key === 'rarity' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="p-4 text-left">Traits</th>
                    <th className="p-4 text-left">
                      <button 
                        onClick={() => requestSort('level')}
                        className="flex items-center gap-2 hover:text-green-400 transition-colors"
                      >
                        Level
                        {sortConfig.key === 'level' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="p-4 text-left">
                      <button 
                        onClick={() => requestSort('experience')}
                        className="flex items-center gap-2 hover:text-green-400 transition-colors"
                      >
                        Experience
                        {sortConfig.key === 'experience' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="p-4 text-left">
                      <button 
                        onClick={() => requestSort('energy')}
                        className="flex items-center gap-2 hover:text-green-400 transition-colors"
                      >
                        Energy
                        {sortConfig.key === 'energy' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="p-4 text-left">
                      <button 
                        onClick={() => requestSort('created_at')}
                        className="flex items-center gap-2 hover:text-green-400 transition-colors"
                      >
                        Created
                        {sortConfig.key === 'created_at' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[...plants].sort(sortPlants).map((plant, index) => (
                    <tr 
                      key={plant.id} 
                      className="group hover:bg-white/5 transition-colors animate-fade-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <td className="p-4">
                        <div className="font-bold">{plant.species}</div>
                        <div className="text-xs text-gray-400">
                          {plant.genome.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="p-4">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${getRarityColor(plant.rarity)}`}>
                          {getRarityIcon(plant.rarity)}
                          {plant.rarity}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-2">
                          {plant.traits.map((trait, i) => (
                            <span 
                              key={i}
                              className="px-2 py-1 bg-white/5 rounded text-xs"
                            >
                              {trait}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Brain className="w-4 h-4 text-purple-400" />
                          {plant.level}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-400" />
                          {plant.experience}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-blue-400" />
                          {plant.energy}%
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm">{formatDate(plant.created_at || '')}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}